import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Phone, Target, Heart, Lightbulb, HandshakeIcon } from 'lucide-react';
import { ImpactStats } from '../components/ImpactStats';
const values = [{
  icon: Heart,
  title: 'People First',
  description: 'Behind every claim is a person and a family. We never lose sight of that.'
}, {
  icon: Target,
  title: 'Outcomes-Driven',
  description: 'We measure success by results — both clinical recovery and financial savings.'
}, {
  icon: Lightbulb,
  title: 'Continuously Evolving',
  description: 'We invest in technology and training to stay ahead of an ever-changing industry.'
}, {
  icon: HandshakeIcon,
  title: 'Partnership Mindset',
  description: 'We win when our clients win. Long-term relationships are at the core of our business.'
}];
export function About() {
  return <div className="bg-brand-paper" data-id="element-353" data-mp="1lrojbrllp8hf">
      {/* Hero */}
      <section className="relative bg-brand-navy pt-40 pb-24 overflow-hidden" data-id="element-354" data-mp="1uitjjhp71xyc">
        <div className="absolute inset-0 opacity-10" data-id="element-355" data-mp="1pjgv4siocitn">
          <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(#C9A24B 1px, transparent 1px)',
          backgroundSize: '40px 40px'
        }} data-id="element-356" data-mp="1pks0uxvok0ne"></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-357" data-mp="2dfuoj2iqzy2a">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="max-w-3xl" data-id="element-358" data-mp="28ht4ae9l5sp8">
            <div className="inline-flex items-center space-x-2 bg-white/5 border border-white/10 rounded-full px-4 py-1.5 mb-6" data-id="element-359" data-mp="2w6m4imx8qmpn">
              <span className="text-xs font-bold tracking-widest text-brand-gold uppercase" data-id="element-360" data-mp="2r9dk0fome6l8">
                About Arissa
              </span>
            </div>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-serif font-medium text-white leading-[1.1] mb-6 text-balance" data-id="element-361" data-mp="1svyiua8k8pz2">
              We help employers{' '}
              <span className="text-brand-gold italic" data-id="element-362" data-mp="1xt86bkce2rk9">navigate care</span> for
              their injured workers.
            </h1>
            <p className="text-xl text-white/80 leading-relaxed font-light max-w-2xl" data-id="element-363" data-mp="27le79jkmcjbl">
              For over 14 years, Arissa Cost Solutions has been a trusted
              national partner to insurance carriers, TPAs, and employers —
              combining intelligent technology with deeply personal service.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Mission / Story */}
      <section className="py-24 bg-white" data-id="element-364" data-mp="m8b2op9pgpgw">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-16 items-center" data-id="element-365" data-mp="m9m80tisu5a4">
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.7
        }} data-id="element-366" data-mp="r74gyjwtgki4">
            <h2 className="text-brand-gold font-semibold tracking-wider uppercase text-sm mb-3" data-id="element-367" data-mp="4yz4l8141yzb">
              Our Story
            </h2>
            <h3 className="text-4xl font-serif text-brand-navy mb-6 leading-tight" data-id="element-368" data-mp="12lpg73lj0vax">
              Built on a simple belief: care matters.
            </h3>
            <p className="text-lg text-gray-600 leading-relaxed mb-5" data-id="element-369" data-mp="snrvgq2vlgn2">
              Arissa Cost Solutions was founded on the conviction that workers'
              compensation managed care could be done better — with more
              humanity, more technology, and more accountability.
            </p>
            <p className="text-lg text-gray-600 leading-relaxed mb-5" data-id="element-370" data-mp="39c5o77jwyzcb">
              Today we provide highly integrated and comprehensive managed care
              services for workers' compensation and liability claims through
              our exceptional team, customized programs, and proprietary
              technology platform.
            </p>
            <p className="text-lg text-gray-600 leading-relaxed" data-id="element-371" data-mp="r7mc45b13ci4">
              Our values say it all:{' '}
              <span className="font-semibold text-brand-navy italic" data-id="element-372" data-mp="w2t9ywhm9mz4">
                People First, Continuously Evolving, Partnership Mindset.
              </span>{' '}
              We help our partners modernize their claims approach, scale their
              capabilities, and align medical management with measurable
              outcomes.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.7
        }} className="relative" data-id="element-373" data-mp="1etkki1dcbjf3">
            <div className="absolute -inset-4 bg-brand-gold/20 rounded-2xl transform -rotate-2" data-id="element-374" data-mp="3n27h1kcunyer"></div>
            <img src="https://cdn.magicpatterns.com/uploads/qMQhfLfnoQwMKypqdvPGfN/ArissaCS-home-health-ancillary-services-irvine-ca-2.jpg" alt="Home health nurse caring for an older couple" className="relative rounded-2xl shadow-2xl h-[560px] w-full object-cover" data-id="element-375" data-mp="5tkkv6z5tljb" />
          </motion.div>
        </div>
      </section>

      {/* Stats — shared banner, consistent with home page */}
      <ImpactStats data-id="element-376" data-mp="3j38gsd22cptn" />

      {/* Values */}
      <section className="py-24 bg-brand-paper" data-id="element-377" data-mp="3eaf9pij4h5ee">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-378" data-mp="3e8m3u5gc2p7g">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-379" data-mp="2qjtfau21jjmv">
            <h2 className="text-brand-gold font-semibold tracking-wider uppercase text-sm mb-3" data-id="element-380" data-mp="2lm9sv6v6w0ju">
              What We Stand For
            </h2>
            <h3 className="text-4xl md:text-5xl font-serif text-brand-navy" data-id="element-381" data-mp="3b9nj1babqffs">
              Our values guide every decision.
            </h3>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-382" data-mp="3k1yzq5fmyoik">
            {values.map((v, i) => {
            const Icon = v.icon;
            return <motion.div key={v.title} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: i * 0.1
            }} className="bg-white rounded-xl p-8 shadow-sm hover:shadow-xl transition-shadow border border-gray-100" data-id="element-383" data-mp="3p3ekrxhtcv0y" data-mpi={i}>
                  <div className="w-14 h-14 rounded-lg bg-brand-navy text-brand-gold flex items-center justify-center mb-6" data-id="element-384" data-mp="263fan0nnkwpo">
                    <Icon className="w-7 h-7" data-id="element-385" data-mp="214ucr7vvip4e" />
                  </div>
                  <h4 className="font-serif font-bold text-xl text-brand-navy mb-3" data-id="element-386" data-mp="1wcknzv6hvv3p">
                    {v.title}
                  </h4>
                  <p className="text-gray-600 leading-relaxed" data-id="element-387" data-mp="1rd7yt3s5ibxy">
                    {v.description}
                  </p>
                </motion.div>;
          })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-brand-navy py-20 relative overflow-hidden" data-id="element-388" data-mp="1de8do63ukf6j">
        <div className="absolute top-0 left-0 w-full h-1 bg-brand-gold" data-id="element-389" data-mp="1degq5ww25ih7"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10" data-id="element-390" data-mp="18gf7hd2jnl69">
          <h2 className="text-4xl md:text-5xl font-serif text-white mb-6 leading-tight" data-id="element-391" data-mp="37r6m0k5zl1r8">
            Let's build a stronger program together.
          </h2>
          <p className="text-lg text-white/80 mb-10 max-w-2xl mx-auto" data-id="element-392" data-mp="2bnsnp4xj4n61">
            Connect with our team to learn how Arissa Cost Solutions can
            transform your workers' compensation outcomes.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-393" data-mp="22w0k9e6xaeay">
            <Link to="/contact" className="bg-brand-gold text-brand-navy px-8 py-4 rounded font-semibold text-lg hover:bg-white transition-colors w-full sm:w-auto flex items-center justify-center" data-id="element-394" data-mp="26wu867kd7t3u">
              Get in Touch <ArrowRight className="w-5 h-5 ml-2" data-id="element-395" data-mp="1xxyxai6dluu7" />
            </Link>
            <a href="tel:8552601053" className="flex items-center justify-center text-white border border-white/30 px-8 py-4 rounded font-semibold text-lg hover:bg-white/10 transition-colors w-full sm:w-auto" data-id="element-396" data-mp="1jxo8dkommqbo">
              <Phone className="w-5 h-5 mr-2" data-id="element-397" data-mp="3e8t84dwleilq" /> 855.260.1053
            </a>
          </div>
        </div>
      </section>
    </div>;
}